import * as _ from "lodash";

/**
 * Gets the property value at path of object. If the resolved value is undefined the defaultValue is used
 * in its place.
 *
 * @param object The object to query.
 * @param path The path of the property to get.
 * @param defaultValue The value returned if the resolved value is undefined.
 * @return Returns the resolved value.
 */
function get<TObject extends object, TKey extends keyof TObject>(
    object: TObject,
    path: TKey | [TKey]
): TObject[TKey];

/**
 * @see _.get
 */
function get<TObject extends object, TKey extends keyof TObject>(
    object: TObject | null | undefined,
    path: TKey | [TKey]
): TObject[TKey] | undefined;

/**
 * @see _.get
 */
function get<TObject extends object, TKey extends keyof TObject, TDefault>(
    object: TObject | null | undefined,
    path: TKey | [TKey],
    defaultValue: TDefault
): TObject[TKey] | TDefault;

/**
 * @see _.get
 */
function get<T>(
    object: _.NumericDictionary<T>,
    path: number
): T;

/**
 * @see _.get
 */
function get<T>(
    object: _.NumericDictionary<T> | null | undefined,
    path: number
): T | undefined;

/**
 * @see _.get
 */
function get<T, TDefault>(
    object: _.NumericDictionary<T> | null | undefined,
    path: number,
    defaultValue: TDefault
): T | TDefault;

/**
 * @see _.get
 */
function get<TDefault>(
    object: null | undefined,
    path: _.PropertyPath,
    defaultValue: TDefault
): TDefault;

/**
 * @see _.get
 */
function get(
    object: null | undefined,
    path: _.PropertyPath
): undefined;

function get(
    object: any,
    path: _.PropertyPath,
    defaultValue?: any
): any;
function get(object, path, defaultValue?) {
    if (defaultValue !== undefined) {
        return _.get(object, path, defaultValue);
    } else {
        return _.get(object, path);
    }
}

/**
 * Checks if `path` is a direct property of `object`.
 *
 * @category Object
 * @param object The object to query.
 * @param path The path to check.
 * @returns Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = { 'a': { 'b': { 'c': 3 } } };
 * var other = _.create({ 'a': _.create({ 'b': _.create({ 'c': 3 }) }) });
 *
 * _.has(object, 'a');
 * // => true
 *
 * _.has(object, 'a.b.c');
 * // => true
 *
 * _.has(object, ['a', 'b', 'c']);
 * // => true
 *
 * _.has(other, 'a');
 * // => false
 */
function has<T>(
    object: T,
    path: _.PropertyPath
): boolean {
    return _.has(object, path);
}

/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @category Object
 * @param object The object to query.
 * @param path The path to check.
 * @returns Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': _.create({ 'c': 3 }) }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b.c');
 * // => true
 *
 * _.hasIn(object, ['a', 'b', 'c']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */
function hasIn<T>(
    object: T,
    path:_.PropertyPath
): boolean {
    return _.hasIn(object, path)
};

/**
 * Creates an object composed of the picked `object` properties.
 *
 * @category Object
 * @param object The source object.
 * @param [props] The property names to pick, specified
 *  individually or in arrays.
 * @returns Returns the new object.
 * @example
 *
 * var object = { 'a': 1, 'b': '2', 'c': 3 };
 *
 * _.pick(object, ['a', 'c']);
 * // => { 'a': 1, 'c': 3 }
 */
function pick<T extends object, U extends keyof T>(
    object: T,
    ...props: Array<_.Many<U>>
): Pick<T, U>;
function pick<T>(
    object: T | null | undefined,
    ...props: _.PropertyPath[]
): _.PartialDeep<T>;
function pick(object, ...props) {
    return _.pick(object, ...props);
}

/**
* The opposite of `_.pick`; this method creates an object composed of the
* own and inherited enumerable properties of `object` that are not omitted.
*
* @category Object
* @param object The source object.
* @param [paths] The property names to omit, specified
*  individually or in arrays..
* @returns Returns the new object.
* @example
*
* var object = { 'a': 1, 'b': '2', 'c': 3 };
*
* _.omit(object, ['a', 'c']);
* // => { 'b': '2' }
*/
function omit<T extends _.AnyKindOfDictionary | object>(
    object: T | null | undefined,
    ...paths: Array<_.Many<_.PropertyName>>
): T | _.PartialObject<T> {
    return _.omit(object, ...paths);
}


/**
 * Creates an array of the own enumerable property names of object.
 *
 * Note: Non-object values are coerced to objects. See the ES spec for more details.
 *
 * @param object The object to query.
 * @return Returns the array of property names.
 */
function keys(object?: any): string[] {
    return _.keys(object);
}

/**
 * An alternative to _.reduce; this method transforms object to a new accumulator object which is the result of
 * running each of its own enumerable properties through iteratee, with each invocation potentially mutating
 * the accumulator object. The iteratee is invoked with four arguments: (accumulator,
 * value, key, object). Iteratee functions may exit iteration early by explicitly returning false.
 *
 * @param object The object to iterate over.
 * @param iteratee The function invoked per iteration.
 * @param accumulator The custom accumulator value.
 * @return Returns the accumulated value.
 */
function transform<T, TResult>(
    object: T[],
    iteratee: _.MemoVoidArrayIterator<T, TResult[]>,
    accumulator?: TResult[]
): TResult[] {
    return _.transform(object, iteratee, accumulator);
}

/**
 * Recursively merges own and inherited enumerable properties of source
 * objects into the destination object, skipping source properties that resolve
 * to `undefined`. Array and plain object properties are merged recursively.
 * Other objects and value types are overridden by assignment. Source objects
 * are applied from left to right. Subsequent sources overwrite property
 * assignments of previous sources.
 *
 * **Note:** This method mutates `object`.
 *
 * @category Object
 * @param object The destination object.
 * @param [sources] The source objects.
 * @returns Returns `object`.
 * @example
 *
 * var users = {
 *   'data': [{ 'user': 'barney' }, { 'user': 'fred' }]
 * };
 *
 * var ages = {
 *   'data': [{ 'age': 36 }, { 'age': 40 }]
 * };
 *
 * _.merge(users, ages);
 * // => { 'data': [{ 'user': 'barney', 'age': 36 }, { 'user': 'fred', 'age': 40 }] }
 */
function merge<TObject, TSource>(
    object: TObject,
    source: TSource
): TObject & TSource;

/**
 * @see _.merge
 */
function merge<TObject, TSource1, TSource2>(
    object: TObject,
    source1: TSource1,
    source2: TSource2
): TObject & TSource1 & TSource2;

/**
 * @see _.merge
 */
function merge<TObject, TSource1, TSource2, TSource3>(
    object: TObject,
    source1: TSource1,
    source2: TSource2,
    source3: TSource3
): TObject & TSource1 & TSource2 & TSource3;

/**
 * @see _.merge
 */
function merge<TObject, TSource1, TSource2, TSource3, TSource4>(
    object: TObject,
    source1: TSource1,
    source2: TSource2,
    source3: TSource3,
    source4: TSource4
): TObject & TSource1 & TSource2 & TSource3 & TSource4;

/**
 * @see _.merge
 */
function merge(
    object: any,
    ...otherArgs: any[]
): any;
function merge(object, ...otherArgs) {
    return _.merge(object, ...otherArgs);
}

/**
* @see _.extend
*/
function extend<TObject, TSource>(
    object: TObject,
    source: TSource
): TObject & TSource {
    return _.extend(object, source);
}

/**
 * The opposite of _.mapValues; this method creates an object with the same values as object and keys generated
 * by running each own enumerable property of object through iteratee.
 *
 * @param object The object to iterate over.
 * @param iteratee The function invoked per iteration.
 * @return Returns the new mapped object.
 */
function mapKeys<T>(
object: _.List<T> | null | undefined,
iteratee?: _.ListIteratee<T>
): _.Dictionary<T>;

/**
 * @see _.mapKeys
 */
function mapKeys<T extends object>(
object: T | null | undefined,
iteratee?: _.ObjectIteratee<T>
): _.Dictionary<T[keyof T]>;

function mapKeys (object, iteratee) {
    return _.mapKeys(object, iteratee)
}

/**
 * The opposite of `_.pickBy`; this method creates an object composed of the
 * own and inherited enumerable properties of `object` that `predicate`
 * doesn't return truthy for.
 *
 * @category Object
 * @param object The source object.
 * @param [predicate=_.identity] The function invoked per property.
 * @returns Returns the new object.
 * @example
 *
 * var object = { 'a': 1, 'b': '2', 'c': 3 };
 *
 * _.omitBy(object, _.isNumber);
 * // => { 'b': '2' }
 */
function omitBy<T>(object: _.Dictionary<T> | null | undefined, predicate?: _.ValueKeyIteratee<T>): _.Dictionary<T>;
/**
 * @see _.omitBy
 */
function omitBy<T>(object: _.NumericDictionary<T> | null | undefined, predicate?: _.ValueKeyIteratee<T>): _.NumericDictionary<T>;
/**
 * @see _.omitBy
 */
function omitBy<T extends object>(object: T | null | undefined, predicate: _.ValueKeyIteratee<T[keyof T]>): _.PartialObject<T>;

function omitBy(object, predicate?) {
    return _.omitBy(object, predicate)
}

export const ObjectUtil = {
    get, has, hasIn, pick, omit, keys, transform, merge, extend, mapKeys, omitBy
}

export default ObjectUtil;